/**
 * Shared delivery-delay resolver helpers (Track A).
 * Mirrors snippets/delivery-delay-resolve.liquid for JS surfaces.
 * Liquid pre-resolves variant metafield (fallback: product.custom.delivery_delay) into maps.
 */
(function (global) {
  /**
   * @param {string | Date} value
   * @returns {string}
   */
  function toIsoDate(value) {
    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) return "";
      const y = value.getFullYear();
      const m = String(value.getMonth() + 1).padStart(2, "0");
      const d = String(value.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    }

    const raw = String(value || "").trim();
    if (!raw) return "";
    const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : "";
  }

  /**
   * @param {number} monthIndex
   * @param {'long' | 'short'} type
   * @returns {string}
   */
  function monthName(monthIndex, type) {
    try {
      const locale = document.documentElement.lang || undefined;
      return new Intl.DateTimeFormat(locale, { month: type, timeZone: "UTC" }).format(
        new Date(Date.UTC(2020, monthIndex, 1)),
      );
    } catch (_e) {
      const long = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      const short = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return (type === "short" ? short : long)[monthIndex] || "";
    }
  }

  /**
   * @param {string} template
   * @param {Record<string, string | number>} vars
   * @returns {string}
   */
  function interpolate(template, vars) {
    let out = String(template || "");
    const map = vars && typeof vars === "object" ? vars : {};

    for (const [key, value] of Object.entries(map)) {
      const safe = String(value ?? "");
      out = out.replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, "g"), safe);
      out = out.replace(new RegExp(`__${key.toUpperCase()}__`, "g"), safe);
    }

    if (Object.prototype.hasOwnProperty.call(map, "date")) {
      out = out.replace(/__DATE__/g, String(map.date ?? ""));
    }
    if (Object.prototype.hasOwnProperty.call(map, "date_short")) {
      out = out.replace(/__DATE_SHORT__/g, String(map.date_short ?? ""));
    }
    if (Object.prototype.hasOwnProperty.call(map, "names")) {
      out = out.replace(/__NAMES__/g, String(map.names ?? ""));
    }
    if (Object.prototype.hasOwnProperty.call(map, "count")) {
      out = out.replace(/__COUNT__/g, String(map.count ?? ""));
    }

    return out;
  }

  const DeliveryDelay = {
    /**
     * @param {{ shipFrom?: string, reason?: string, name?: string, mode?: string } | null | undefined} data
     * @param {Date} [now]
     * @returns {{ shipFrom: string, reason: string, name: string, mode: string } | null}
     */
    resolveItem(data, now = new Date()) {
      if (!data || typeof data !== "object") return null;

      const mode = String(data.mode || "").trim().toLowerCase();
      if (!mode || mode === "off") return null;

      const shipFrom = String(data.shipFrom || "").trim();
      if (!shipFrom) return null;

      const todayIso = toIsoDate(now);
      const shipIso = toIsoDate(shipFrom);
      if (!shipIso || shipIso <= todayIso) return null;

      // Map entries are pre-resolved in Liquid (forced / OOS). Treat present map data as active.
      return {
        shipFrom: shipIso,
        reason: String(data.reason || "").trim(),
        name: String(data.name || "").trim(),
        mode,
      };
    },

    /**
     * @param {Array<{ shipFrom?: string, reason?: string, name?: string, mode?: string } | null | undefined>} components
     * @param {Date} [now]
     * @returns {{ state: 'NONE' | 'PARTIAL' | 'FULL', shipFrom?: string, names: string[], count: number }}
     */
    resolveGroup(components, now = new Date()) {
      const list = Array.isArray(components) ? components : [];
      const delayed = [];

      for (const item of list) {
        const resolved = DeliveryDelay.resolveItem(item, now);
        if (resolved) delayed.push(resolved);
      }

      if (delayed.length === 0) {
        return { state: "NONE", names: [], count: 0 };
      }

      let maxShipFrom = delayed[0].shipFrom;
      for (const d of delayed) {
        if (d.shipFrom > maxShipFrom) maxShipFrom = d.shipFrom;
      }

      return {
        state: delayed.length === list.length ? "FULL" : "PARTIAL",
        shipFrom: maxShipFrom,
        names: delayed.map((d) => d.name).filter(Boolean),
        count: delayed.length,
      };
    },

    /**
     * @param {string[]} names
     * @param {string} [andWord]
     * @returns {string}
     */
    formatNames(names, andWord = "and") {
      const list = (Array.isArray(names) ? names : []).map((n) => String(n || "").trim()).filter(Boolean);
      if (list.length === 0) return "";
      if (list.length === 1) return list[0];
      if (list.length === 2) return `${list[0]} ${andWord} ${list[1]}`;
      return `${list.slice(0, -1).join(", ")} ${andWord} ${list[list.length - 1]}`;
    },

    /**
     * Formats an ISO date using Shopify-like format tokens from locale keys.
     * Supports: %Y %m %d %B %b %-d
     * @param {string} isoDate
     * @param {string} format
     * @returns {string}
     */
    formatDate(isoDate, format) {
      const iso = toIsoDate(isoDate);
      if (!iso || !format) return "";

      const parts = iso.split("-").map((p) => Number(p));
      if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return iso;

      const [year, month, day] = parts;
      const date = new Date(Date.UTC(year, month - 1, day));
      const monthLong = monthName(date.getUTCMonth(), "long");
      const monthShort = monthName(date.getUTCMonth(), "short");

      return String(format)
        .replace(/%Y/g, String(year))
        .replace(/%m/g, String(month).padStart(2, "0"))
        .replace(/%-d/g, String(day))
        .replace(/%d/g, String(day).padStart(2, "0"))
        .replace(/%B/g, monthLong)
        .replace(/%b/g, monthShort);
    },

    interpolate,

    /** Cart/order attribute key for delayed item titles (bundle expand workaround). */
    ORDER_ITEMS_ATTRIBUTE: "_delivery_delay_items",

    /**
     * Join delayed item titles for the order attribute (machine-friendly, comma-separated).
     * @param {string[]} names
     * @returns {string}
     */
    formatItemsAttribute(names) {
      return (Array.isArray(names) ? names : [])
        .map((n) => String(n || "").trim())
        .filter(Boolean)
        .join(", ");
    },

    /**
     * Sync cart-level attributes on a product/bundle form (survive Cart Transform expand).
     * Only used for Bundle Builder / product.type Bundle — not for regular line properties.
     *
     * @param {HTMLFormElement} form
     * @param {{ name: string, value: string, items?: string } | null} payload
     */
    syncBundleOrderAttributes(form, payload) {
      if (!(form instanceof HTMLFormElement)) return;

      const noteSel = "[data-delivery-delay-order-attribute]";
      const itemsSel = "[data-delivery-delay-order-items-attribute]";

      if (!payload || !payload.name || !payload.value) {
        form.querySelector(noteSel)?.remove();
        form.querySelector(itemsSel)?.remove();
        return;
      }

      let noteInput = form.querySelector(noteSel);
      if (!(noteInput instanceof HTMLInputElement)) {
        noteInput = document.createElement("input");
        noteInput.type = "hidden";
        noteInput.setAttribute("data-delivery-delay-order-attribute", "");
        form.appendChild(noteInput);
      }
      noteInput.name = `attributes[${payload.name}]`;
      noteInput.value = payload.value;

      const itemsText = String(payload.items || "").trim();
      let itemsInput = form.querySelector(itemsSel);
      if (!itemsText) {
        itemsInput?.remove();
        return;
      }

      if (!(itemsInput instanceof HTMLInputElement)) {
        itemsInput = document.createElement("input");
        itemsInput.type = "hidden";
        itemsInput.setAttribute("data-delivery-delay-order-items-attribute", "");
        form.appendChild(itemsInput);
      }
      itemsInput.name = `attributes[${this.ORDER_ITEMS_ATTRIBUTE}]`;
      itemsInput.value = itemsText;
    },
  };

  const DELAY_ATTRIBUTE_KEYS = ["_Versandhinweis", "_Shipping note", DeliveryDelay.ORDER_ITEMS_ATTRIBUTE];

  /**
   * @param {Record<string, unknown> | null | undefined} properties
   * @returns {boolean}
   */
  function hasDelayLineProperty(properties) {
    if (!properties || typeof properties !== "object") return false;
    return DELAY_ATTRIBUTE_KEYS.some((key) => String(properties[key] || "").trim() !== "");
  }

  /**
   * Titles listed in `_delivery_delay_items` (comma-separated).
   * @param {Record<string, unknown> | null | undefined} attrs
   * @returns {string[]}
   */
  function listedDelayItemNames(attrs) {
    if (!attrs || typeof attrs !== "object") return [];
    return String(attrs[DeliveryDelay.ORDER_ITEMS_ATTRIBUTE] || "")
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
  }

  /**
   * After Cart Transform expands a type=Bundle parent, line properties are dropped.
   * Match remaining component rows by product title from `_delivery_delay_items`.
   * @param {{ product_title?: string, title?: string } | null | undefined} item
   * @param {string} name
   * @returns {boolean}
   */
  function cartItemMatchesDelayName(item, name) {
    const needle = String(name || "").trim().toLowerCase();
    if (!needle) return false;
    const haystacks = [item && item.product_title, item && item.title]
      .map((value) => String(value || "").trim().toLowerCase())
      .filter(Boolean);
    return haystacks.some((text) => text === needle || text.includes(needle) || needle.includes(text));
  }

  /**
   * True when delayed lines still exist: visible line property, or expanded bundle
   * components whose titles match `_delivery_delay_items`.
   * @param {{ items?: Array<{ properties?: Record<string, unknown>, product_title?: string, title?: string }>, attributes?: Record<string, unknown> } | null | undefined} cart
   * @returns {boolean}
   */
  function cartHasDelayedItems(cart) {
    const items = cart && Array.isArray(cart.items) ? cart.items : [];
    if (items.some((item) => hasDelayLineProperty(item && item.properties))) return true;

    const listed = listedDelayItemNames(cart && cart.attributes);
    if (listed.length === 0) return false;
    return items.some((item) => listed.some((name) => cartItemMatchesDelayName(item, name)));
  }

  /**
   * @param {{ items?: Array<{ properties?: Record<string, unknown>, product_title?: string, title?: string }>, attributes?: Record<string, unknown> } | null | undefined} cart
   * @returns {string[]}
   */
  function staleDelayAttributeKeys(cart) {
    const attrs = cart && cart.attributes && typeof cart.attributes === "object" ? cart.attributes : {};
    const present = DELAY_ATTRIBUTE_KEYS.filter((key) => String(attrs[key] || "").trim() !== "");
    if (present.length === 0) return [];
    return cartHasDelayedItems(cart) ? [] : present;
  }

  let pruneInFlight = false;

  /**
   * Drop leftover bundle note attributes when no delayed line (or expanded component) remains.
   * @param {{ items?: Array<{ properties?: Record<string, unknown>, product_title?: string, title?: string }>, attributes?: Record<string, unknown> } | null | undefined} cart
   * @returns {Promise<void>}
   */
  async function pruneStaleOrderAttributes(cart) {
    if (pruneInFlight) return;
    const keys = staleDelayAttributeKeys(cart);
    if (keys.length === 0) return;

    pruneInFlight = true;
    try {
      const attributes = {};
      for (const key of keys) attributes[key] = "";

      const route =
        global.themeVariables && global.themeVariables.routes && global.themeVariables.routes.cartUrl
          ? global.themeVariables.routes.cartUrl
          : "/cart";

      await fetch(`${route}/update.js`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attributes }),
      }).then(async (response) => {
        if (!response.ok || !global.document) return;
        const updatedCart = await response.json();
        global.document.documentElement.dispatchEvent(
          new CustomEvent("Cart:Changed", { bubbles: true, detail: { cart: updatedCart } }),
        );
      });
    } catch (_e) {
      // Keep add-to-cart / cart UI working if the cleanup request fails.
    } finally {
      pruneInFlight = false;
    }
  }

  /**
   * @param {Event} event
   */
  function onCartEvent(event) {
    const cart = event && event.detail ? event.detail.cart : null;
    if (cart) pruneStaleOrderAttributes(cart);
  }

  async function pruneFromCartJs() {
    try {
      const route =
        global.themeVariables && global.themeVariables.routes && global.themeVariables.routes.cartUrl
          ? global.themeVariables.routes.cartUrl
          : "/cart";
      const response = await fetch(`${route}.js`, { credentials: "same-origin" });
      if (!response.ok) return;
      const cart = await response.json();
      await pruneStaleOrderAttributes(cart);
    } catch (_e) {}
  }

  function bindStaleAttributeCleanup() {
    const root = global.document && global.document.documentElement;
    if (!root) return;
    root.addEventListener("Cart:Refresh", onCartEvent);
    root.addEventListener("Cart:Changed", onCartEvent);
    pruneFromCartJs();
  }

  if (global.document) {
    if (global.document.readyState === "loading") {
      global.document.addEventListener("DOMContentLoaded", bindStaleAttributeCleanup);
    } else {
      bindStaleAttributeCleanup();
    }
  }

  global.DeliveryDelay = DeliveryDelay;
})(typeof window !== "undefined" ? window : globalThis);